import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import BackgroundEffects from '../../../components/layout/BackgroundEffects/BackgroundEffects'
import Header from '../../../components/layout/Header/Header'
import Card from '../../../components/common/Card/Card'
import Button from '../../../components/common/Button/Button'
import { getCurrentUser, logout, getUserData } from '../../../services/authService'
import {
  getLeavePolicy,
  updateLeavePolicy,
  getPolicySections,
  formatPolicyDate
} from '../../../services/leavePolicyService'
import PolicySection from '../components/PolicySection'
import EditPolicyModal from '../components/EditPolicyModal'

const LeavePolicy = () => {
  const navigate = useNavigate()

  // Auth & User State
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)

  // Policy State
  const [policy, setPolicy] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  const sections = getPolicySections()

  // Load user data and policy
  useEffect(() => {
    loadUserAndPolicy()
  }, [])

  const loadUserAndPolicy = async () => {
    try {
      setLoading(true)
      setError('')

      const currentUser = getCurrentUser()
      if (!currentUser) {
        navigate('/login')
        return
      }

      setUser(currentUser)

      // Get user data from Firestore
      const userDataResult = await getUserData(currentUser.uid)

      if (!userDataResult.success) {
        setError('Failed to load user data')
        setLoading(false)
        return
      }

      setUserData(userDataResult.data)

      // Check if user is admin
      const adminStatus =
        userDataResult.data.role === 'super_admin' ||
        userDataResult.data.role === 'admin'
      setIsAdmin(adminStatus)

      // Load leave policy
      const result = await getLeavePolicy()
      if (result.success) {
        setPolicy(result.data)
      } else {
        setError(result.error || 'Failed to load leave policy')
      }
    } catch (error) {
      console.error('Load error:', error)
      setError('An error occurred while loading data')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    const result = await logout()
    if (result.success) {
      navigate('/login')
    }
  }

  const handleBack = () => {
    navigate('/dashboard')
  }

  const handleOpenModal = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleSubmitPolicy = async (formData) => {
    setIsSubmitting(true)
    setError('')
    setSuccessMessage('')

    try {
      const adminName = userData?.fullName || user?.email || 'Admin'
      const result = await updateLeavePolicy(formData, adminName)

      if (result.success) {
        // Reload policy to get updated data with timestamp
        const updatedPolicy = await getLeavePolicy()
        if (updatedPolicy.success) {
          setPolicy(updatedPolicy.data)
        }

        setSuccessMessage('Leave policy updated successfully!')
        handleCloseModal()

        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to update leave policy')
      }
    } catch (error) {
      console.error('Submit error:', error)
      setError('An error occurred while updating the policy')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Loading State
  if (loading) {
    return (
      <div className="psyber-container psyber-portal-container">
        <BackgroundEffects />
        <Header
          user={userData || user}
          onLogout={handleLogout}
          showBackButton={true}
          onBack={handleBack}
          title="Leave Policy"
        />
        <Card style={{ textAlign: 'center', padding: '3rem' }}>
          <i
            className="fas fa-spinner fa-spin"
            style={{
              fontSize: '3rem',
              color: 'var(--psyber-primary)',
              marginBottom: '1rem'
            }}
          ></i>
          <p style={{ color: 'var(--psyber-text-secondary)' }}>Loading leave policy...</p>
        </Card>
      </div>
    )
  }

  // Error State (Critical Error)
  if (error && !policy) {
    return (
      <div className="psyber-container psyber-portal-container">
        <BackgroundEffects />
        <Header
          user={userData || user}
          onLogout={handleLogout}
          showBackButton={true}
          onBack={handleBack}
          title="Leave Policy"
        />
        <Card style={{ textAlign: 'center', padding: '3rem' }}>
          <i
            className="fas fa-exclamation-triangle"
            style={{
              fontSize: '3rem',
              color: 'var(--psyber-error)',
              marginBottom: '1rem'
            }}
          ></i>
          <h3 style={{ marginBottom: '1rem', color: 'var(--psyber-error)' }}>Error</h3>
          <p style={{ color: 'var(--psyber-text-secondary)', marginBottom: '2rem' }}>{error}</p>
          <Button variant="primary" icon="fas fa-redo" onClick={loadUserAndPolicy}>
            Retry
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="psyber-container psyber-portal-container">
      <BackgroundEffects />

      <Header
        user={userData || user}
        onLogout={handleLogout}
        showBackButton={true}
        onBack={handleBack}
        title="Leave Policy"
      />

      {/* Success Message */}
      {successMessage && (
        <Card
          style={{
            marginBottom: '1.5rem',
            background: 'var(--psyber-success-bg)',
            border: '2px solid var(--psyber-success)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-check-circle" style={{ color: 'var(--psyber-success)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-success)', fontWeight: 600 }}>{successMessage}</p>
          </div>
        </Card>
      )}

      {/* Error Message */}
      {error && policy && (
        <Card
          style={{
            marginBottom: '1.5rem',
            background: 'var(--psyber-error-bg)',
            border: '2px solid var(--psyber-error)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-exclamation-circle" style={{ color: 'var(--psyber-error)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-error)', fontWeight: 600 }}>{error}</p>
          </div>
        </Card>
      )}

      {/* Header Card */}
      <Card style={{ marginBottom: '2rem' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: 'var(--psyber-font-size-2xl)',
              color: 'var(--psyber-text)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="fas fa-book-open"></i>
              Company Leave Policy
            </h2>
            <p style={{
              margin: '0.5rem 0 0 0',
              fontSize: 'var(--psyber-font-size-sm)',
              color: 'var(--psyber-text-secondary)'
            }}>
              Review our comprehensive leave policies and entitlements
            </p>
          </div>

          {/* Admin Edit Button */}
          {isAdmin && (
            <Button
              variant="primary"
              icon="fas fa-edit"
              onClick={handleOpenModal}
            >
              Edit Policy
            </Button>
          )}
        </div>
      </Card>

      {/* Policy Sections */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {sections.map((section) => (
          <PolicySection
            key={section.key}
            title={section.title}
            icon={section.icon}
            content={policy?.[section.key] || 'No information available'}
          />
        ))}
      </div>

      {/* Last Updated Footer */}
      {policy && (
        <Card style={{
          background: 'var(--psyber-info-bg)',
          border: '1px solid var(--psyber-info)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            fontSize: 'var(--psyber-font-size-sm)',
            color: 'var(--psyber-text-secondary)'
          }}>
            <i className="fas fa-clock" style={{ color: 'var(--psyber-info)' }}></i>
            <span>
              Last updated by <strong>{policy.lastUpdatedBy}</strong> on{' '}
              <strong>{formatPolicyDate(policy.lastUpdatedAt)}</strong>
            </span>
          </div>
        </Card>
      )}

      {/* Help Section for Employees */}
      {!isAdmin && (
        <Card style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <i className="fas fa-question-circle"></i> Need Help?
          </h3>
          <div
            style={{
              fontSize: 'var(--psyber-font-size-sm)',
              color: 'var(--psyber-text-secondary)',
              lineHeight: 1.6
            }}
          >
            <p style={{ marginBottom: '0.75rem' }}>
              <strong>Applying for Leave:</strong> Visit the Time Off module to submit leave requests.
            </p>
            <p style={{ marginBottom: '0.75rem' }}>
              <strong>Check Balance:</strong> Your current leave balance is available in the Time Off module.
            </p>
            <p style={{ margin: 0 }}>
              <strong>Questions?</strong> Contact HR at{' '}
              <a href="mailto:hello@psybernova.com" style={{ color: 'var(--psyber-primary)', textDecoration: 'none' }}>
                hello@psybernova.com
              </a>
            </p>
          </div>
        </Card>
      )}

      {/* Edit Policy Modal */}
      <EditPolicyModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitPolicy}
        currentPolicy={policy}
        isLoading={isSubmitting}
      />
    </div>
  )
}

export default LeavePolicy
